import React, { useMemo, useState } from 'react';
import { FunctionDataType, FunctionDefinition, FunctionReferenceValue } from '@glueapp/graph-types';
import { Button } from '@/components/migrated/Button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ChevronDown, Edit2, Trash2, Wrench, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { computeCompatibilityInfo, createFixedFunction } from './functionCompatibility';
import { TypeSignatureDisplay } from './TypeSignatureDisplay';
import { FunctionItem } from './FunctionItem';
import { CreateFunctionButton, CreateFunctionForm } from './CreateFunctionForm';

interface Props {
    portName: string;
    expectedType: FunctionDataType;
    currentValue: FunctionReferenceValue | undefined;
    availableFunctions: FunctionDefinition[];
    onFunctionSelect: (functionId: string) => void;
    onFunctionClear: () => void;
    onCreateFunction: (functionName: string) => void;
    onEditFunction?: (functionId: string) => void;
    onDeleteFunction?: (functionId: string) => void;
    onFixFunctionCompatibility?: (fixedFunction: FunctionDefinition) => void;
}

export const FunctionPortEditor: React.FC<Props> = ({
    portName,
    expectedType,
    currentValue,
    availableFunctions,
    onFunctionSelect,
    onFunctionClear,
    onCreateFunction,
    onEditFunction,
    onDeleteFunction,
    onFixFunctionCompatibility,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [functionToDelete, setFunctionToDelete] = useState<FunctionDefinition | null>(null);

    const functionsWithCompatibility = useMemo(() => {
        if (expectedType.kind !== 'function') {
            return [];
        }

        return availableFunctions.map(func => computeCompatibilityInfo(func, expectedType));
    }, [availableFunctions, expectedType]);

    const sortedFunctions = useMemo(() => {
        return [...functionsWithCompatibility].sort((a, b) => {
            if (a.compatible !== b.compatible) {
                return a.compatible ? -1 : 1;
            }
            return a.function.name.localeCompare(b.function.name);
        });
    }, [functionsWithCompatibility]);

    const handleCreateFunction = (name: string) => {
        onCreateFunction(name);
        setIsCreating(false);
    };

    const selectedFunction = currentValue ? availableFunctions.find(f => f.id === currentValue.functionId) : undefined;
    const selectedFunctionInfo =
        selectedFunction && expectedType.kind === 'function' ? computeCompatibilityInfo(selectedFunction, expectedType) : undefined;

    const handleDeleteConfirm = () => {
        if (functionToDelete && onDeleteFunction) {
            // If deleting the currently selected function, clear the selection first
            if (currentValue?.functionId === functionToDelete.id) {
                onFunctionClear();
            }
            onDeleteFunction(functionToDelete.id);
            setFunctionToDelete(null);
        }
    };

    return (
        <div className="flex flex-col gap-2 p-2 border border-border rounded-lg bg-muted/30">
            <div className="font-medium text-sm text-foreground">{portName}</div>

            {selectedFunction && selectedFunctionInfo ? (
                <div className="p-3 rounded-lg border bg-primary/10 border-primary">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                        <span className="font-medium">{selectedFunction.name}</span>
                        <div className="flex items-center gap-2">
                            <span className="text-xs px-1.5 py-0.5 rounded bg-primary text-primary-foreground">Selected</span>
                            {selectedFunctionInfo.compatible ? (
                                <span className="text-xs px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300">
                                    Compatible
                                </span>
                            ) : (
                                <span className="text-xs px-1.5 py-0.5 rounded bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300">
                                    Incompatible
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center justify-between gap-2 mt-2">
                        <div className="flex items-center gap-2">
                            {onEditFunction && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => onEditFunction(selectedFunction.id)}
                                    className="h-7 text-xs gap-1"
                                    title="Edit function"
                                >
                                    <Edit2 className="w-3 h-3" />
                                    Edit
                                </Button>
                            )}
                        </div>
                        {!selectedFunctionInfo.compatible && onFixFunctionCompatibility && (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onFixFunctionCompatibility(createFixedFunction(selectedFunction, selectedFunctionInfo))}
                                className="h-7 text-xs gap-1"
                                title="Adds any missing ports and changes types of incompatible ports"
                            >
                                <Wrench className="w-3 h-3" />
                                Make Compatible
                            </Button>
                        )}
                    </div>

                    {expectedType.kind === 'function' && <TypeSignatureDisplay func={selectedFunction} expectedType={expectedType} />}

                    <div className="flex items-center justify-between gap-2 mt-2">
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={onFunctionClear}
                            className="h-7 text-xs gap-1"
                            title="Deselect function"
                        >
                            <X className="w-3 h-3" />
                            Deselect
                        </Button>
                        {onDeleteFunction && (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setFunctionToDelete(selectedFunction)}
                                className="h-7 text-xs gap-1 text-destructive hover:text-destructive"
                                title="Delete function"
                            >
                                <Trash2 className="w-3 h-3" />
                                Delete
                            </Button>
                        )}
                    </div>
                </div>
            ) : (
                <Collapsible open={isOpen} onOpenChange={setIsOpen}>
                    <CollapsibleTrigger asChild>
                        <Button variant="outline" className="w-full justify-between">
                            <span className="text-muted-foreground">
                                {availableFunctions.length === 0 ? 'No functions available' : 'Select a function'}
                            </span>
                            <ChevronDown className={cn('w-4 h-4 transition-transform', isOpen && 'rotate-180')} />
                        </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-2 space-y-2">
                        {sortedFunctions.map(info => (
                            <FunctionItem
                                key={info.function.id}
                                info={info}
                                isSelected={currentValue?.functionId === info.function.id}
                                onSelect={() => {
                                    onFunctionSelect(info.function.id);
                                    setIsOpen(false);
                                }}
                                onEdit={onEditFunction ? () => onEditFunction(info.function.id) : undefined}
                                onDelete={onDeleteFunction ? () => setFunctionToDelete(info.function) : undefined}
                                onFix={
                                    onFixFunctionCompatibility && !info.compatible
                                        ? () => onFixFunctionCompatibility(createFixedFunction(info.function, info))
                                        : undefined
                                }
                                expectedType={expectedType}
                            />
                        ))}

                        <div className="pt-2 border-t border-border">
                            {isCreating ? (
                                <CreateFunctionForm onSubmit={handleCreateFunction} onCancel={() => setIsCreating(false)} />
                            ) : (
                                <CreateFunctionButton onClick={() => setIsCreating(true)} />
                            )}
                        </div>
                    </CollapsibleContent>
                </Collapsible>
            )}

            {/* Delete confirmation dialog */}
            <Dialog open={functionToDelete !== null} onOpenChange={open => !open && setFunctionToDelete(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete Function</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete the function &#34;{functionToDelete?.name}&#34;? This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setFunctionToDelete(null)}>
                            Cancel
                        </Button>
                        <Button variant="destructive" onClick={handleDeleteConfirm}>
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
